/**
 * The result cards — what a student sees after a check-in, however it arrived.
 *
 * Shared by the scan screen and the typed check-in (FindScreen) so a typed
 * check-in looks identical to a scanned one. Moved here verbatim from
 * ScanScreen; the styles stay in ScanScreen.css, the dark band's stylesheet,
 * which every screen that renders these imports.
 */

import type { CSSProperties } from "react";
import {
  firstNameOf,
  formatInstant,
  formatTime,
  formatTimeRange,
  type AmbiguousData,
  type CheckInData,
  type ScanResult,
  type VisitData,
} from "../api/kiosk";
import { useSettledClick } from "../hooks/useSettledClick";

/** How long a result stays up before the kiosk returns to the idle prompt. */
export const DISMISS_MS = 4000;
/** Ambiguous results need long enough for a child to read and decide. */
export const CHOICE_DISMISS_MS = 15000;

export function ResultCard({
  result,
  onChoose,
  settleMs = 0,
}: {
  result: ScanResult;
  onChoose: (batchId: number) => void;
  /**
   * Ignore taps on the class choices for this long after they appear (see
   * useSettledClick). Off by default — ScanScreen never passes it; the typed
   * check-in does, because there a double-tap on "Yes" lands on a choice.
   */
  settleMs?: number;
}) {
  switch (result.kind) {
    case "checked_in":
      return <CheckedIn data={result.data} />;
    case "already_checked_in":
      return <AlreadyCheckedIn data={result.data} />;
    case "ambiguous":
      return <Ambiguous data={result.data} onChoose={onChoose} settleMs={settleMs} />;
    case "visit_in":
      return <VisitIn data={result.data} />;
    case "visit_out":
      return <VisitOut data={result.data} />;
    case "rejected":
      return (
        <div className="result is-bad" role="alert">
          <span className="result-mark" aria-hidden="true">
            ✕
          </span>
          <p className="result-message">{result.message}</p>
          <Timer ms={DISMISS_MS} />
        </div>
      );
    case "too_early":
      // Amber, not red: the student is in the right place, just ahead of the
      // window. The server's message carries the exact times.
      return (
        <div className="result is-warn" role="status">
          <span className="result-mark" aria-hidden="true">
            ⏱
          </span>
          <p className="result-lede">See you soon</p>
          <p className="result-message">{result.message}</p>
          {result.next && <p className="result-meta">{result.next.label}</p>}
          <Timer ms={DISMISS_MS} />
        </div>
      );
    case "unavailable":
      return (
        <div className="result is-bad" role="alert">
          <span className="result-mark" aria-hidden="true">
            ⚡
          </span>
          <p className="result-lede">No connection</p>
          <p className="result-message">{result.message}</p>
          <Timer ms={DISMISS_MS} />
        </div>
      );
    case "slow_scan":
      return (
        <div className="result is-warn" role="alert">
          <span className="result-mark" aria-hidden="true">
            ↻
          </span>
          <p className="result-lede">Try again</p>
          <p className="result-message">{result.message}</p>
          <Timer ms={DISMISS_MS} />
        </div>
      );
  }
}

function CheckedIn({ data }: { data: CheckInData }) {
  return (
    <div className="result is-ok" role="status">
      <svg
        className="result-mark"
        viewBox="0 0 48 48"
        width="1em"
        height="1em"
        fill="none"
        aria-hidden="true"
      >
        <circle cx="24" cy="24" r="21" stroke="currentColor" strokeWidth="3.5" />
        <path
          d="m14 24.5 7 7 13-15"
          stroke="currentColor"
          strokeWidth="4.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <p className="result-lede">Checked in</p>
      <h1 className="result-name">{firstNameOf(data?.student_name)}</h1>
      <p className="result-detail">{data?.batch?.label}</p>
      <p className="result-meta">
        {formatTimeRange(data?.batch?.start_time, data?.batch?.end_time, " – ")}
      </p>
      {data?.makeup && <span className="makeup-tag">Makeup class</span>}
      <NextSessionNudge next={data?.next_session} />
      <Timer ms={DISMISS_MS} />
    </div>
  );
}

/**
 * Competition drop-in visits (D-016): these students come most days, so an
 * off-day scan checks them in and out of the BRANCH rather than a class. The
 * card looks like the regular green check-in — the distinction matters to
 * reports, not to a kid at the door. A re-scan within the guard window comes
 * back as visit_in again, so double-scanning at arrival shows the same card.
 */
function VisitIn({ data }: { data: VisitData }) {
  const at = formatInstant(data?.checked_in_at);
  return (
    <div className="result is-ok" role="status">
      <svg
        className="result-mark"
        viewBox="0 0 48 48"
        width="1em"
        height="1em"
        fill="none"
        aria-hidden="true"
      >
        <circle cx="24" cy="24" r="21" stroke="currentColor" strokeWidth="3.5" />
        <path
          d="m14 24.5 7 7 13-15"
          stroke="currentColor"
          strokeWidth="4.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <p className="result-lede">Checked in</p>
      <h1 className="result-name">{firstNameOf(data?.student_name)}</h1>
      <p className="result-detail">Competitions</p>
      {at && <p className="result-meta">Arrived at {at}</p>}
      <p className="result-next">Scan again on your way out</p>
      <Timer ms={DISMISS_MS} />
    </div>
  );
}

function VisitOut({ data }: { data: VisitData }) {
  const at = formatInstant(data?.checked_out_at);
  return (
    <div className="result is-ok" role="status">
      <span className="result-mark" aria-hidden="true">
        👋
      </span>
      <p className="result-lede">Checked out</p>
      <h1 className="result-name">{firstNameOf(data?.student_name)}</h1>
      <p className="result-message">
        {at ? `See you next time — out at ${at}` : "See you next time"}
      </p>
      <Timer ms={DISMISS_MS} />
    </div>
  );
}

/**
 * D-015: a student with another session later today is told, never
 * auto-marked — a door scan attests arrival, not future attendance.
 */
function NextSessionNudge({ next }: { next?: CheckInData["next_session"] }) {
  if (!next) return null;
  return (
    <p className="result-next">
      See you at {next.label} · {formatTime(next.start_time)}
    </p>
  );
}

function AlreadyCheckedIn({ data }: { data: CheckInData }) {
  const at = formatInstant(data?.scanned_in_at);
  return (
    <div className="result is-warn" role="status">
      <span className="result-mark" aria-hidden="true">
        ✓
      </span>
      <h1 className="result-headline">{firstNameOf(data?.student_name)}</h1>
      <p className="result-message">
        {at ? `Already checked in at ${at}` : "Already checked in"}
      </p>
      <p className="result-meta">{data?.batch?.label}</p>
      <NextSessionNudge next={data?.next_session} />
      <Timer ms={DISMISS_MS} />
    </div>
  );
}

function Ambiguous({
  data,
  onChoose,
  settleMs,
}: {
  data: AmbiguousData;
  onChoose: (batchId: number) => void;
  settleMs: number;
}) {
  const settled = useSettledClick(settleMs);
  return (
    <div className="result is-ask" role="group" aria-label="Choose your class">
      <p className="result-lede">{firstNameOf(data?.student_name)} — which class?</p>
      <div className="choices">
        {data.choices.map((choice) => (
          <button
            key={choice.batch_id}
            type="button"
            className="choice"
            onClick={(event) => {
              if (settled(event)) onChoose(choice.batch_id);
            }}
          >
            <span className="choice-label">{choice.label}</span>
            <span className="choice-time">
              {formatTimeRange(choice.start_time, choice.end_time, " – ")}
            </span>
          </button>
        ))}
      </div>
      <Timer ms={CHOICE_DISMISS_MS} />
    </div>
  );
}

function Timer({ ms }: { ms: number }) {
  return (
    <span
      className="result-timer"
      style={{ "--dismiss-ms": `${ms}ms` } as CSSProperties}
      aria-hidden="true"
    />
  );
}
